import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import ICalendarDay from "interfaces/ICalendarDay";
import parseISO from "date-fns/parseISO";
import format from "date-fns/format";
import addDays from "date-fns/addDays";
import differenceInCalendarDays from "date-fns/differenceInCalendarDays";
import isSameDay from "date-fns/isSameDay";
import isBefore from "date-fns/isBefore";
import isAfter from "date-fns/isAfter";
import isFirstDayOfMonth from "date-fns/isFirstDayOfMonth";
import isPast from "date-fns/isPast";
import isToday from "date-fns/isToday";

export interface IDateRange {
  startDate: Date;
  endDate: Date;
  repeatance?: racehorse360.FacilityBlackoutRepeatance;
}

const isBetween = (date: Date, dateFrom: Date, dateTo: Date): boolean => {
  return (
    isSameDay(date, dateFrom) ||
    isSameDay(date, dateTo) ||
    (isAfter(date, dateFrom) && isBefore(date, dateTo))
  );
};

export const isInRange = (date: Date, range: IDateRange): boolean => {
  if (
    range.repeatance ===
    racehorse360.FacilityBlackoutRepeatance.FACILITY_BLACKOUT_REPEATANCE_WEEKLY
  ) {
    if (isBefore(date, range.startDate)) return false;

    const rangeLength = differenceInCalendarDays(
      range.endDate,
      range.startDate
    );
    if (rangeLength >= 7) return true;

    const startDay = range.startDate.getDay();
    const endDay = range.endDate.getDay();
    const day = date.getDay();
    if (endDay >= startDay) {
      return day >= startDay && day <= endDay;
    } else {
      return day >= startDay || day <= endDay;
    }
  }

  return isBetween(date, range.startDate, range.endDate);
};

export const getCalendarDay = (dateText: string): ICalendarDay => {
  const date = parseISO(dateText);
  const dateLabel = format(date, isFirstDayOfMonth(date) ? "MMM d" : "d");
  const isTodayDate = isToday(date);
  const isPastDate = !isTodayDate && isPast(date);

  return {
    date,
    label: dateLabel,
    isToday: isTodayDate,
    isPast: isPastDate,
    facilityBlackout: null
  };
};

export const isBlackedOut = (
  date: Date,
  blackouts: racehorse360.IFacilityBlackout[]
): boolean => {
  let excluded = false;
  return blackouts.some(blackout => {
    if (excluded) {
      return false;
    }
    if (
      blackout.facilityBlackoutExcludedDates.some(excluded =>
        isSameDay(date, parseISO(excluded.date))
      )
    ) {
      excluded = true;
      return false;
    }
    if (
      blackout.dateRepeatTo &&
      isAfter(date, parseISO(blackout.dateRepeatTo))
    ) {
      return false;
    }
    return isInRange(date, {
      startDate: parseISO(blackout.dateFrom),
      endDate: parseISO(blackout.dateTo),
      repeatance: blackout.repeatance
    });
  });
};

export const getFacilityBlackoutsCalendar = (
  sourceFacilityBlackouts: racehorse360.IFacilityBlackout[],
  sourceCalendar: ICalendarDay[]
): ICalendarDay[] => {
  const REPEATANCE_DOES_NOT_REPEAT =
    racehorse360.FacilityBlackoutRepeatance
      .FACILITY_BLACKOUT_REPEATANCE_DOES_NOT_REPEAT;
  const REPEATANCE_WEEKLY =
    racehorse360.FacilityBlackoutRepeatance.FACILITY_BLACKOUT_REPEATANCE_WEEKLY;

  const facilityBlackouts = sourceFacilityBlackouts.map(facilityBlackout => {
    return {
      ...facilityBlackout,
      isIntersectSingleBlackout: false
    };
  });

  const calendar = sourceCalendar.map(day => {
    return {
      ...day,
      facilityBlackout: null
    };
  });

  const lastCalendarDay = calendar[calendar.length - 1];

  const getCalendarDays = (
    dateFrom: Date,
    dateTo: Date,
    dateMax: Date,
    excludedDates: Date[]
  ) => {
    return calendar.filter(day => {
      return (
        isBetween(day.date, dateFrom, dateTo) &&
        (!dateMax || !isAfter(day.date, dateMax)) &&
        !excludedDates.some(excludedDate => {
          return isSameDay(excludedDate, day.date);
        })
      );
    });
  };

  for (const facilityBlackout of facilityBlackouts) {
    const blackoutDateRepeatTo = facilityBlackout.dateRepeatTo
      ? parseISO(facilityBlackout.dateRepeatTo)
      : null;
    let blackoutDateFrom = parseISO(facilityBlackout.dateFrom);
    let blackoutDateTo = parseISO(facilityBlackout.dateTo);

    const excludedDates = facilityBlackout.facilityBlackoutExcludedDates.map(
      facilityBlackoutExcludedDate => {
        return parseISO(facilityBlackoutExcludedDate.date);
      }
    );

    while (
      !isAfter(blackoutDateFrom, lastCalendarDay.date) &&
      (!blackoutDateRepeatTo ||
        !isAfter(blackoutDateFrom, blackoutDateRepeatTo))
    ) {
      const calendarDays = getCalendarDays(
        blackoutDateFrom,
        blackoutDateTo,
        blackoutDateRepeatTo,
        excludedDates
      );

      calendarDays.forEach(calendarDay => {
        if (facilityBlackout.repeatance === REPEATANCE_DOES_NOT_REPEAT) {
          if (calendarDay.facilityBlackout) {
            if (calendarDay.facilityBlackout.repeatance === REPEATANCE_WEEKLY) {
              calendarDay.facilityBlackout.isIntersectSingleBlackout = true;
            }
          } else {
            calendarDay.facilityBlackout = facilityBlackout;
          }
        } else if (facilityBlackout.repeatance === REPEATANCE_WEEKLY) {
          if (calendarDay.facilityBlackout) {
            if (calendarDay.facilityBlackout.repeatance !== REPEATANCE_WEEKLY) {
              calendarDay.facilityBlackout = facilityBlackout;
            }
          } else {
            calendarDay.facilityBlackout = facilityBlackout;
          }
        }
      });

      if (facilityBlackout.repeatance === REPEATANCE_WEEKLY) {
        blackoutDateFrom = addDays(blackoutDateFrom, 7);
        blackoutDateTo = addDays(blackoutDateTo, 7);
      } else {
        break;
      }
    }
  }

  return calendar;
};
